import { useMutation } from '@apollo/client';
import { FlagTransition } from '@generated/graphql';
import { UserContext } from '@providers/userContextProvider';
import type { Theme } from 'hephaestus/core';
import { useSnackbar } from 'hephaestus/web';
import type { TFunction } from 'i18next';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TRANSITION_FLAG, UPDATE_FLAG_NOTES } from './flags.queries';
import {
  areFlagNotesAtLeastAsFresh,
  buildUpdateFlagNotesInput,
  getReconFlagNoteAuthorName,
  hasUnsavedFlagNoteDraft,
} from './reconFlagNotes';
import type { ReconFlagRow } from './reconFlags.types';

type UseReconFlagsDrawerInput = {
  theme: Theme;
  t: TFunction;
  refetchAfterFlagMutation: () => Promise<unknown>;
  refetchGrid: () => Promise<unknown>;
};

type DrawerTransitionAction = 'resolve' | 'dismiss';

export const useReconFlagsDrawer = ({
  theme,
  t,
  refetchAfterFlagMutation,
  refetchGrid,
}: UseReconFlagsDrawerInput) => {
  const { enqueueSnackbar } = useSnackbar();
  const { userContext } = useContext(UserContext);
  const noteAuthorName = getReconFlagNoteAuthorName(userContext);
  const [activeDrawerFlag, setActiveDrawerFlag] = useState<ReconFlagRow | null>(
    null,
  );
  const [drawerNotesDraft, setDrawerNotesDraft] = useState('');
  const savedFlagNotesByIdRef = useRef(
    new Map<string, NonNullable<ReconFlagRow['notes']>>(),
  );

  const [pendingDrawerAction, setPendingDrawerAction] =
    useState<DrawerTransitionAction | null>(null);

  const [transitionFlag] = useMutation(TRANSITION_FLAG);
  const [updateNotes] = useMutation(UPDATE_FLAG_NOTES);

  const persistNotesDraftIfNeeded = useCallback(async (): Promise<boolean> => {
    if (!activeDrawerFlag) {
      return false;
    }
    if (!hasUnsavedFlagNoteDraft(drawerNotesDraft)) {
      return true;
    }
    try {
      const { data, errors } = await updateNotes({
        variables: {
          input: buildUpdateFlagNotesInput(
            activeDrawerFlag.id,
            drawerNotesDraft,
            noteAuthorName,
          ),
        },
      });
      if (errors?.length || !data?.updateFlagNotes?.flag?.id) {
        enqueueSnackbar(t('reconFlags.drawer.notesSaveError'), {
          variant: 'error',
        });
        return false;
      }
      const savedNotes = data.updateFlagNotes.flag.notes ?? [];
      savedFlagNotesByIdRef.current.set(activeDrawerFlag.id, savedNotes);
      setActiveDrawerFlag((prev) =>
        prev == null ? null : { ...prev, notes: savedNotes },
      );
      setDrawerNotesDraft('');
      return true;
    } catch {
      enqueueSnackbar(t('reconFlags.drawer.notesSaveError'), {
        variant: 'error',
      });
      return false;
    }
  }, [
    activeDrawerFlag,
    drawerNotesDraft,
    noteAuthorName,
    enqueueSnackbar,
    t,
    updateNotes,
  ]);

  const transitionDrawerFlag = useCallback(
    async (
      action: DrawerTransitionAction,
      transition: FlagTransition,
      messages: { success: string; error: string },
    ): Promise<void> => {
      if (!activeDrawerFlag) {
        return;
      }
      setPendingDrawerAction(action);
      try {
        const notesSaved = await persistNotesDraftIfNeeded();
        if (!notesSaved) {
          return;
        }
        const { data: transitionData, errors: transitionErrors } =
          await transitionFlag({
            variables: {
              input: {
                flagId: activeDrawerFlag.id,
                transition,
              },
            },
          });
        if (
          transitionErrors?.length ||
          !transitionData?.transitionFlag?.flag?.id
        ) {
          enqueueSnackbar(messages.error, { variant: 'error' });
          return;
        }
        enqueueSnackbar(messages.success, { variant: 'success' });
        await refetchAfterFlagMutation();
        setActiveDrawerFlag(null);
      } catch {
        enqueueSnackbar(messages.error, { variant: 'error' });
      } finally {
        setPendingDrawerAction(null);
      }
    },
    [
      activeDrawerFlag,
      persistNotesDraftIfNeeded,
      refetchAfterFlagMutation,
      enqueueSnackbar,
      transitionFlag,
    ],
  );

  const onResolveDrawerFlag = useCallback(
    () =>
      transitionDrawerFlag('resolve', FlagTransition.Fixed, {
        success: t('reconFlags.drawer.resolveSuccess'),
        error: t('reconFlags.drawer.resolveError'),
      }),
    [t, transitionDrawerFlag],
  );

  const onDismissDrawerFlag = useCallback(
    () =>
      transitionDrawerFlag('dismiss', FlagTransition.Trivial, {
        success: t('reconFlags.drawer.dismissSuccess'),
        error: t('reconFlags.drawer.dismissError'),
      }),
    [t, transitionDrawerFlag],
  );

  const openDrawer = useCallback((row: ReconFlagRow) => {
    const cachedNotes = savedFlagNotesByIdRef.current.get(row.id);
    if (
      cachedNotes == null ||
      areFlagNotesAtLeastAsFresh(row.notes, cachedNotes)
    ) {
      if (cachedNotes != null) {
        savedFlagNotesByIdRef.current.delete(row.id);
      }
      setActiveDrawerFlag(row);
      return;
    }
    setActiveDrawerFlag({ ...row, notes: cachedNotes });
  }, []);

  const onNotesSaved = useCallback(
    async (notes: ReconFlagRow['notes']) => {
      const savedNotes = notes ?? [];
      setActiveDrawerFlag((prev) => {
        if (prev == null) {
          return null;
        }
        savedFlagNotesByIdRef.current.set(prev.id, savedNotes);
        return { ...prev, notes: savedNotes };
      });
      setDrawerNotesDraft('');
      try {
        await refetchGrid();
      } catch {
        // Best-effort grid sync; savedFlagNotesByIdRef keeps reopen correct.
      }
    },
    [refetchGrid],
  );

  useEffect(() => {
    setDrawerNotesDraft('');
    setPendingDrawerAction(null);
  }, [activeDrawerFlag?.id]);

  const resolveLoading = pendingDrawerAction === 'resolve';
  const dismissLoading = pendingDrawerAction === 'dismiss';

  const drawerProps = useMemo(
    () => ({
      theme,
      flag: activeDrawerFlag,
      isOpen: activeDrawerFlag !== null,
      onClose: () => setActiveDrawerFlag(null),
      notesDraft: drawerNotesDraft,
      onNotesDraftChange: setDrawerNotesDraft,
      onNotesSaved,
      onResolve: onResolveDrawerFlag,
      onDismiss: onDismissDrawerFlag,
      resolveLoading,
      dismissLoading,
    }),
    [
      activeDrawerFlag,
      dismissLoading,
      drawerNotesDraft,
      onDismissDrawerFlag,
      onNotesSaved,
      onResolveDrawerFlag,
      resolveLoading,
      theme,
    ],
  );

  return {
    drawerProps,
    openDrawer,
  };
};
